import type { ActivePhaseView } from './gameScreenState'

export interface TutorialHelpContext {
  phase: ActivePhaseView
  isMyTurn: boolean
  selectedFromId: string | null
  selectedToId: string | null
  freeFortifyPending: boolean
  /** A valid three-card set the viewer may turn in during placement. */
  hasTradeableCardSet?: boolean
  continuousAttackEnabled?: boolean
  currentPlayerName?: string | null
  /** Team games: placement and fortify reach teammates, attacks on them warn. */
  teams?: boolean
  /** Team games: the selected attack target is a teammate's territory. */
  targetIsTeammate?: boolean
}

export interface TutorialHelpCopy {
  title: string
  body: string
}

/**
 * Contextual copy for the optional live-game tutorial strip. Keep this pure so
 * every transition can be tested without rendering the game screen.
 */
export function tutorialHelpCopy(context: TutorialHelpContext): TutorialHelpCopy {
  const {
    phase,
    isMyTurn,
    selectedFromId,
    selectedToId,
    freeFortifyPending,
    hasTradeableCardSet = false,
    continuousAttackEnabled = false,
    currentPlayerName,
    teams = false,
    targetIsTeammate = false,
  } = context

  if (phase === 'spectator' || !isMyTurn) {
    return {
      title: currentPlayerName ? `${currentPlayerName}'s turn` : 'Waiting for the next turn',
      body: 'Watch the map and recent activity below. Your action panel will appear when it is your turn.',
    }
  }

  if (phase === 'region-details') {
    return {
      title: 'Region details',
      body: 'Browse the regions and their bonuses. Switch back to Game view when you are ready to play.',
    }
  }

  if (phase === 'claim') {
    return {
      title: 'Claim a territory',
      body: 'Tap an unclaimed territory to claim it and place your starting army.',
    }
  }

  if (phase === 'placement' || phase === 'post-elim-placement') {
    if (hasTradeableCardSet) {
      return {
        title: 'Turn in a card set',
        body: 'You have a valid set. Tap the flashing cards button to select 3 cards to turn-in.',
      }
    }
    return {
      title: phase === 'post-elim-placement' ? 'Place your bonus armies' : 'Place your armies',
      body: teams
        ? "Tap your territories or a teammate's to add armies. When all are placed, tap Commit to start attacking."
        : 'Tap your territories to add armies. When all are placed, tap Commit to start attacking.',
    }
  }

  if (phase === 'attack') {
    if (freeFortifyPending) {
      return {
        title: 'Move armies after conquest',
        body: 'Move extra armies into the territory you just conquered, or tap None to skip.',
      }
    }
    if (selectedFromId && selectedToId && targetIsTeammate) {
      return {
        title: 'Attacking a teammate',
        body: 'This territory belongs to your teammate. You can attack it, but your team loses troops either way. Tap elsewhere to pick an enemy instead.',
      }
    }
    if (selectedFromId && selectedToId) {
      return {
        title: continuousAttackEnabled ? 'Continuous attack' : 'Resolve the attack',
        body: continuousAttackEnabled
          ? 'Attacks until your army is down to the number beside the toggle. Bad dice luck can burn through your troops! 🔥'
          : 'Pick 1–3 dice and tap Attack. Tap elsewhere on the map or close the panel to exit the attack.',
      }
    }
    if (selectedFromId) {
      return {
        title: 'Choose a target',
        body: teams
          ? "Tap an adjacent enemy territory to attack it. Teammates' territories can be attacked too, after a warning."
          : 'Tap an adjacent enemy territory to attack it.',
      }
    }
    return {
      title: 'Choose an attack source',
      body: 'Tap one of your territories with at least 2 armies to attack from, or tap Fortify when you are done attacking.',
    }
  }

  if (phase === 'fortify') {
    if (selectedFromId && selectedToId) {
      return {
        title: 'Move armies',
        body: 'Choose how many armies to move, then tap Move.',
      }
    }
    if (selectedFromId) {
      return {
        title: 'Choose a destination',
        body: teams
          ? 'Tap an adjacent territory you or a teammate control to move armies into.'
          : 'Tap an adjacent territory you control to move armies into.',
      }
    }
    return {
      title: 'Fortify your position',
      body: teams
        ? "Tap one of your territories with 2+ armies to move from. You can move into a teammate's territory too. You can also go back to attack, or End Turn."
        : 'Tap one of your territories with 2+ armies to move from. You can also go back to attack, or End Turn.',
    }
  }

  return {
    title: 'Your turn',
    body: 'Use the action controls below the map to play this phase.',
  }
}
